import { useState, useEffect, useRef, useCallback } from "react";
import photo1 from "../assets/IMG_2101.jpeg";
import photo2 from "../assets/artist-photo.png";
import photo3 from "../assets/IMG_2104.jpeg";
import photo4 from "../assets/IMG_2105.jpeg";
import "../styles/intro.style.css";

const PHOTOS = [photo1, photo2, photo3, photo4];
const TEXT = "MOODE.ART";
const LETTERS = TEXT.split("");
const CYCLES = 5;

interface ScatterLetter {
  char: string;
  x: number;
  y: number;
  w: number;
  h: number;
  bgPhoto: string;
  bgSize: string;
  bgPos: string;
  tx: string;
  ty: string;
  rot: number;
  delay: number;
  dur: number;
}

interface IntroProps {
  onComplete: () => void;
}

export function Intro({ onComplete }: IntroProps) {
  const [photoIndex, setPhotoIndex] = useState(0);
  const [scatterLetters, setScatterLetters] = useState<ScatterLetter[]>([]);
  const [scattering, setScattering] = useState(false);

  const measureRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const completeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track current photo index in a ref so scatter() can read it without deps
  const photoIndexRef = useRef(0);
  // Keep onComplete stable via ref, synced in an effect (not during render)
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const scatter = useCallback(() => {
    if (!measureRef.current) return;

    const spans = measureRef.current.querySelectorAll<HTMLSpanElement>("span");
    const vw = window.innerWidth;
    const bgSize = `${vw * 1.1}px auto`;
    const activePhoto = PHOTOS[photoIndexRef.current] ?? "";

    const letters: ScatterLetter[] = Array.from(spans).map((span, i) => {
      const rect = span.getBoundingClientRect();
      const angle = Math.random() * Math.PI * 2;
      const dist = 60 + Math.random() * 140;
      const tx = `${Math.cos(angle) * dist}vw`;
      const ty = `${Math.sin(angle) * dist}vh`;
      const rot = (Math.random() - 0.5) * 360;
      const dur = 1200 + Math.random() * 600;
      return {
        char: LETTERS[i] ?? "",
        x: rect.left,
        y: rect.top,
        w: rect.width,
        h: rect.height,
        bgPhoto: activePhoto,
        bgSize,
        bgPos: `${-rect.left}px ${-rect.top}px`,
        tx,
        ty,
        rot,
        delay: i * 30,
        dur,
      };
    });

    setScatterLetters(letters);
    setScattering(true);

    const maxDelay = letters.reduce((m, l) => Math.max(m, l.delay + l.dur), 0);
    completeTimerRef.current = setTimeout(() => onCompleteRef.current(), maxDelay + 400);
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let cycle = 0;
    const interval = setInterval(() => {
      cycle++;
      setPhotoIndex((i) => {
        const next = (i + 1) % PHOTOS.length;
        photoIndexRef.current = next;
        return next;
      });
      if (cycle >= CYCLES) {
        clearInterval(interval);
        scatter();
      }
    }, 1200);

    return () => {
      startedRef.current = false;
      clearInterval(interval);
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
    };
  }, [scatter]);

  return (
    <div className="fixed inset-0 z-200 bg-black flex items-center justify-center">
      {/* Photo layers with background-clip: text */}
      {!scattering && (
        <div className="relative flex items-center justify-center w-full">
          {PHOTOS.map((photo, i) => (
            <div
              key={i}
              className="intro-photo-layer"
              style={{
                backgroundImage: `url(${photo})`,
                opacity: i === photoIndex ? 1 : 0,
              }}
            >
              {TEXT}
            </div>
          ))}
        </div>
      )}

      {/* Scatter letters */}
      {scattering && scatterLetters.map((l, i) => <ScatterSpan key={i} letter={l} />)}

      {/* Hidden measurement div */}
      <div ref={measureRef} className="intro-measure" aria-hidden="true">
        {LETTERS.map((char, i) => (
          <span key={i}>{char}</span>
        ))}
      </div>
    </div>
  );
}

function ScatterSpan({ letter: l }: { letter: ScatterLetter }) {
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;

    // Double rAF ensures initial state paints before transition fires
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.transform = `translate(${l.tx}, ${l.ty}) rotate(${l.rot}deg) scale(0.2)`;
        el.style.opacity = "0";
      });
    });
  }, [l.tx, l.ty, l.rot]);

  return (
    <span
      ref={spanRef}
      className="scatter-letter"
      style={{
        left: l.x,
        top: l.y,
        width: l.w,
        height: l.h,
        backgroundImage: `url(${l.bgPhoto})`,
        backgroundSize: l.bgSize,
        backgroundPosition: l.bgPos,
        transitionDelay: `${l.delay}ms`,
        transitionDuration: `${l.dur}ms`,
      }}
    >
      {l.char}
    </span>
  );
}
